import React, { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import Autocomplete from '@mui/material/Autocomplete';
import AddIcon from '@mui/icons-material/Add';
import OvTextField from '../atoms/OvTextField';
import OvTextButton from '../atoms/OvTextButton';
import OvAddVideoPlacementDialog from '../organisms/OvAddVideoPlacementDialog';
import { PlacementPickerOption } from '../../../common/model/type/video-placement.types';
import {
  getPlacementPickerOptionKey,
  getPlacementPickerOptions,
  matchesPlacementSearch,
  toPlacementPickerValue,
} from '../../../common/utils/video-placement-utils';
import { useVideoPermissions } from '../../../hooks/use-video-permissions';
import { useVideoPlacements } from '../../../hooks/use-video-placements';
import Colours from '../../../design-system/colours';
import Variables from '../../../design-system/variables';

interface OvVideoPlacementPickerProps {
  id: string;
  label: string;
  value: string;
  onChange: (key: string) => void;
  isDisabled?: boolean;
}

const OvVideoPlacementPicker: FC<OvVideoPlacementPickerProps> = ({
  id,
  label,
  value,
  onChange,
  isDisabled = false,
}) => {
  const { t } = useTranslation();
  const { canCreate } = useVideoPermissions();
  const placements = useVideoPlacements();
  const [isAdding, setIsAdding] = useState(false);

  const selected = useMemo(
    () => toPlacementPickerValue(placements.data, value),
    [placements.data, value]
  );
  const options = getPlacementPickerOptions(placements.data, value, canCreate);

  const getOptionLabel = (option: PlacementPickerOption) => {
    switch (option.kind) {
      case 'none':
        return t('videos.placement.notShown');

      case 'placement':
        return option.description;

      case 'unlisted':
        return option.key;

      case 'add':
        return t('videos.placements.add.option');

      default: {
        const unhandledOption: never = option;

        return unhandledOption;
      }
    }
  };

  const renderOption = (option: PlacementPickerOption) => {
    switch (option.kind) {
      case 'none':
        return getOptionLabel(option);

      case 'placement':
        return (
          <Option>
            <span>{option.description}</span>
            <Key>{option.key}</Key>
          </Option>
        );

      case 'unlisted':
        return <Key>{option.key}</Key>;

      case 'add':
        return (
          <AddOption>
            <AddIcon fontSize="small" aria-hidden="true" />
            {getOptionLabel(option)}
          </AddOption>
        );

      default: {
        const unhandledOption: never = option;

        return unhandledOption;
      }
    }
  };

  const handleChange = (option: PlacementPickerOption) =>
    option.kind === 'add'
      ? setIsAdding(true)
      : onChange(getPlacementPickerOptionKey(option));

  const getHelperText = () => {
    if (placements.isError) {
      return (
        <>
          {t('videos.placement.pickerError')}{' '}
          <OvTextButton type="button" onClick={() => placements.refetch()}>
            {t('common.actions.retry')}
          </OvTextButton>
        </>
      );
    }

    return selected.kind === 'unlisted' && selected.isRetired
      ? t('videos.placement.retired')
      : undefined;
  };

  return (
    <>
      <Autocomplete
        id={id}
        value={selected}
        options={options}
        disableClearable
        disabled={isDisabled}
        loading={placements.isPending}
        loadingText={t('videos.filters.placementsLoading')}
        noOptionsText={t('videos.filters.noPlacementMatches')}
        getOptionLabel={getOptionLabel}
        getOptionDisabled={(option) => option.kind === 'unlisted'}
        isOptionEqualToValue={(option, current) =>
          getPlacementPickerOptionKey(option) ===
          getPlacementPickerOptionKey(current)
        }
        filterOptions={(candidates, { inputValue }) =>
          candidates.filter((option) =>
            matchesPlacementSearch(option, inputValue)
          )
        }
        renderOption={(props, option) => (
          <li {...props} key={getPlacementPickerOptionKey(option)}>
            {renderOption(option)}
          </li>
        )}
        renderInput={(params) => (
          <OvTextField
            {...params}
            size="small"
            label={label}
            placeholder={t('videos.filters.searchPlacements')}
            error={placements.isError}
            helperText={getHelperText()}
          />
        )}
        onChange={(_, option) => handleChange(option)}
      />
      {canCreate && (
        <OvAddVideoPlacementDialog
          isOpen={isAdding}
          placements={placements.data ?? []}
          onAdded={(placement) => onChange(placement.key)}
          onClose={() => setIsAdding(false)}
        />
      )}
    </>
  );
};

export default OvVideoPlacementPicker;

const Option = styled.span`
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow-wrap: anywhere;
`;

const Key = styled.code`
  font-family: monospace;
  font-size: ${Variables.fontSizes.SMALL};
  color: ${Colours.OV_CHARCOAL};
  overflow-wrap: anywhere;
`;

const AddOption = styled.span`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding-top: 0.25rem;
  border-top: 1px solid ${Colours.OV_BORDER_COLOR_V3};
  font-weight: bold;
`;
